import { useEffect } from 'react'
import * as React from 'react'
import useToggle from 'hooks/useToggle'
import { useGlobalExperiencesSearchContext, useGlobalExperiencesSearchResultsTracking } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalExperiencesSearch'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { fetchExperienceCategories } from 'actions/ExperienceActions'
import useQueryParams from 'hooks/useQueryParams'
import { useAppDispatch } from 'hooks/reduxHooks'
import ExperienceSearchHeader from '../../Experiences/ExperiencesSearch/ExperienceSearchHeader'
import { connect } from 'react-redux'
import ExperienceSearchPageFiltersSidebar from 'components/Experiences/ExperiencesSearch/ExperienceSearchPageFiltersSidebar'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import useExperiencesSearchFilters from '../hooks/useExperiencesSearchFilters'
import { GLOBAL_EXPERIENCES_SEARCH_INITIAL_STATE } from 'contexts/Experience/GlobalSearchState'
import { SearchBurnProvider } from 'contexts/SearchPointsContext'
import { type ExperienceLandmarksToggleState } from './ExperienceSearchLandmarksToggle'

interface MappedStateProps {
  search: string
}

interface Props extends MappedStateProps {
  children: React.ReactNode
  searchMode: Experiences.SearchMode
  list: App.ApiCallState<{
    ids: Array<string>
    placeId: string
    listKey?: string
    searchId?: string
    total?: number
    streamEnded?: boolean
  }>
  place?: App.Place
  landmarksToggle?: ExperienceLandmarksToggleState
  isTransfersSearch?: boolean
}

function ExperienceSearchPageContainer({ children, searchMode, list, place, landmarksToggle, isTransfersSearch = false }: Props) {
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const { searchFilters } = useExperiencesSearchFilters()

  const [showFilter, toggleShowFilter] = useToggle()

  const hasCalendarDates = (!!searchFilters.date && searchFilters.date !== 'today') ||
    (!searchFilters.date && !!searchFilters.checkIn && !!searchFilters.checkOut)

  const { globalExperiencesSearchDispatch, globalExperiencesSearchState } = useGlobalExperiencesSearchContext({
    ...GLOBAL_EXPERIENCES_SEARCH_INITIAL_STATE,
    experienceCategories: searchFilters.categoryCodes ?? [],
    experienceCollections: searchFilters.collections ?? [],
    dateSearchOptionId: hasCalendarDates ? DATE_SEARCH_OPTION_IDS.SPECIFIC : DATE_SEARCH_OPTION_IDS.ANYTIME,
  }, queryParams)

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  }, [dispatch])

  const areResultsFetching = !!list.fetching || (
    !!list.searchId &&
    list.ids.length === 0 &&
    !list.streamEnded &&
    !list.error
  )
  useGlobalExperiencesSearchResultsTracking(
    globalExperiencesSearchState,
    list.ids,
    areResultsFetching,
  )

  return <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
    <GlobalSearchContextProvider
      state={globalExperiencesSearchState}
      dispatch={globalExperiencesSearchDispatch}
    >
      <SearchBurnProvider>
        <ExperienceSearchPageFiltersSidebar
          open={showFilter}
          place={place}
          listIds={list.ids}
          onClose={toggleShowFilter}
        />
        <ExperienceSearchHeader
          list={list}
          searchMode={searchMode}
          toggleShowFilter={toggleShowFilter}
          place={place}
          landmarksToggle={landmarksToggle}
          hideFilters={isTransfersSearch}
        />
        {children}
      </SearchBurnProvider>
    </GlobalSearchContextProvider>
  </AnalyticsPageContext.Provider>
}

function mapStateToProps(state: App.State) {
  return {
    search: state.router.location.search,
  }
}

export default connect(mapStateToProps)(ExperienceSearchPageContainer)
